import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { PROFILE_COLUMNS, SupplierProfile, SupplierProfileInput } from '../lib/supplierProfile';

/**
 * Every supplier's profile, keyed by provider id. Used on the merchant side, where the page
 * already lists all suppliers and wants terms for each without a request per card.
 *
 * A failure here leaves the map empty rather than surfacing an error: the terms are an
 * addition to the supplier list and must never be the reason that list fails to render.
 */
export const useSupplierProfiles = (enabled = true) => {
  const [profiles, setProfiles] = useState<Record<string, SupplierProfile>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await (supabase as any).from('provider_profiles').select(PROFILE_COLUMNS);
      if (cancelled) return;
      if (error) console.error('Failed to load supplier profiles:', error);
      const map: Record<string, SupplierProfile> = {};
      (data || []).forEach((row: SupplierProfile) => { map[row.provider_id] = row; });
      setProfiles(map);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [enabled]);

  return { profiles, loaded };
};

/**
 * One supplier's own profile, for the supplier dashboard.
 *
 * `canEdit` comes from user_provider_access (owner or admin, active), the same rule the
 * table's RLS enforces, so the form is read-only for people whose save would be rejected.
 */
export const useOwnSupplierProfile = (providerId: string | null) => {
  const [profile, setProfile] = useState<SupplierProfile | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!providerId) {
      setProfile(null);
      setCanEdit(false);
      setLoaded(false);
      return;
    }
    try {
      setError(null);
      const { data: auth } = await supabase.auth.getUser();
      const [{ data: row, error: pErr }, { data: access }] = await Promise.all([
        (supabase as any).from('provider_profiles').select(PROFILE_COLUMNS).eq('provider_id', providerId).maybeSingle(),
        auth?.user?.id
          ? (supabase as any)
              .from('user_provider_access')
              .select('role, status')
              .eq('provider_id', providerId)
              .eq('auth_user_id', auth.user.id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      if (pErr) throw pErr;
      setProfile(row ?? null);
      setCanEdit(access?.status === 'active' && ['owner', 'admin'].includes(access?.role));
    } catch (e: any) {
      console.error('Failed to load supplier profile:', e);
      setError(e?.message || 'Failed to load the supplier profile');
    } finally {
      setLoaded(true);
    }
  }, [providerId]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (input: SupplierProfileInput): Promise<boolean> => {
    if (!providerId) return false;
    setSaving(true);
    setError(null);
    try {
      // Only the supplier-writable columns are sent: the table grants are per column, so
      // including sync_interval_minutes or updated_at would fail the whole statement.
      // Update-then-insert rather than upsert for the same reason: PostgREST's upsert also
      // SETs the conflict column, and provider_id is deliberately not updatable.
      const table = () => (supabase as any).from('provider_profiles');
      // Plain .select(), not .maybeSingle(): on a write, maybeSingle asks PostgREST for a
      // single object and an update matching no row answers 406, a red console error on
      // every supplier's first save.
      const { data: updated, error: uErr } = await table()
        .update(input)
        .eq('provider_id', providerId)
        .select(PROFILE_COLUMNS);
      if (uErr) throw uErr;
      if (updated?.[0]) {
        setProfile(updated[0]);
        return true;
      }
      // No row came back: either there is none yet, or RLS hid the update from a non-admin.
      // The insert tells the two apart; RLS rejects it for the latter.
      const { data: inserted, error: iErr } = await table()
        .insert({ provider_id: providerId, ...input })
        .select(PROFILE_COLUMNS);
      if (iErr) throw iErr;
      if (inserted?.[0]) setProfile(inserted[0]);
      return true;
    } catch (e: any) {
      console.error('Failed to save supplier profile:', e);
      setError(
        e?.code === '42501' || e?.code === '23505'
          ? 'Only an owner or admin of this supplier account can change the profile.'
          : e?.message || 'Could not save the supplier profile',
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [providerId]);

  return { profile, canEdit, loaded, saving, error, save, reload: load };
};
